/**
 * useTenantStockRealtime - Tempo real ÚNICO de movimentações de estoque por tenant.
 *
 * Todos os modais (Movimentar itens, Equipamentos utilizados da O.S., ficha do
 * Veículo, ficha do Cliente) compartilham UM canal por tenant, com contagem de
 * uso: o canal nasce com o primeiro assinante e é removido quando sai o último.
 * O evento apenas sinaliza mudança; os dados são relidos pelas RPCs oficiais.
 *
 * - Filtro `tenant_id=eq.<tenant>` aplicado no servidor + RLS de stock_movements.
 * - Invalidações agrupadas (300ms) para evitar rajadas de consultas.
 * - Ao reconectar após queda, invalida tudo uma única vez (recupera eventos perdidos).
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-11 v1.1.0 - Também escuta INSERT em stock_homologations (homologação muda situação sem movimentação).
 *   RELEASE: 2026-10-11 v1.0.0 - Criação: substitui 3 canais independentes de stock_movements.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('TenantStockRealtime');

/** Assinante: chaves de cache a invalidar quando houver movimentação */
interface Subscriber { getKeys: () => QueryKey[] }

interface Entry {
  channel: RealtimeChannel;
  subscribers: Set<Subscriber>;
  timer: ReturnType<typeof setTimeout> | null;
  hadError: boolean;
}

/** Registro global: um canal por tenant (exportado para testes) */
export const stockRealtimeRegistry = new Map<string, Entry>();

/** Invalida (agrupado) as chaves de todos os assinantes do tenant */
function flush(entry: Entry, qc: QueryClient) {
  if (entry.timer) clearTimeout(entry.timer);
  entry.timer = setTimeout(() => {
    entry.timer = null;
    const seen = new Set<string>();
    entry.subscribers.forEach((s) => s.getKeys().forEach((key) => {
      const id = JSON.stringify(key);
      if (seen.has(id)) return;
      seen.add(id);
      qc.invalidateQueries({ queryKey: key });
    }));
  }, 300);
}

/** Registra um assinante; devolve a função de saída (remove o canal no último) */
export function subscribeTenantStock(tenantId: string, sub: Subscriber, qc: QueryClient): () => void {
  let entry = stockRealtimeRegistry.get(tenantId);
  if (!entry) {
    const created: Entry = { channel: null as unknown as RealtimeChannel, subscribers: new Set(), timer: null, hadError: false };
    created.channel = supabase
      .channel(`stock-rt-${tenantId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'stock_movements', filter: `tenant_id=eq.${tenantId}` },
        () => { logger.debug('Movimentação recebida'); flush(created, qc); })
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'stock_homologations', filter: `tenant_id=eq.${tenantId}` },
        () => { logger.debug('Homologação recebida'); flush(created, qc); })
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          created.hadError = true;
          logger.warn('Canal de estoque caiu', { status });
        } else if (status === 'SUBSCRIBED' && created.hadError) {
          // Reconexão: recupera o que pode ter sido perdido durante a queda
          created.hadError = false;
          logger.debug('Canal de estoque reconectado; recarregando dados');
          flush(created, qc);
        }
      });
    stockRealtimeRegistry.set(tenantId, created);
    entry = created;
  }
  entry.subscribers.add(sub);
  const current = entry;
  return () => {
    current.subscribers.delete(sub);
    if (current.subscribers.size === 0) {
      if (current.timer) clearTimeout(current.timer);
      supabase.removeChannel(current.channel);
      stockRealtimeRegistry.delete(tenantId);
    }
  };
}

/**
 * Hook: enquanto `enabled`, invalida `keys` a cada movimentação de estoque do tenant.
 * As chaves são lidas por referência (sem recriar a assinatura a cada render).
 */
export function useTenantStockRealtime(keys: QueryKey[], enabled: boolean = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const qc = useQueryClient();
  const keysRef = useRef(keys);
  keysRef.current = keys;

  useEffect(() => {
    if (!enabled || !tenantId) return;
    return subscribeTenantStock(tenantId, { getKeys: () => keysRef.current }, qc);
  }, [enabled, tenantId, qc]);
}
